import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Play } from 'lucide-react'
import { Button, ButtonLink } from '@/components/generals/button'
import { GradientWash } from '@/components/generals/gradient-wash'
import { LiveDot } from '@/components/generals/live-dot'
import { LaneCard } from '@/components/home/lane-card'
import { LiveTicker } from '@/components/home/live-ticker'
import { FlowPlayerDialog } from '@/components/home/flow-player-dialog'

export const HomeHero: FC = (): ReactNode => {
    const [playerOpen, setPlayerOpen] = useState(false)

    return (
        <section
            id='hero'
            data-header-theme='light'
            aria-labelledby='hero-title'
            className='relative isolate overflow-hidden pt-28 sm:pt-36 xl:pt-40'
        >
            <GradientWash variant='hero' />

            <div className='shell relative grid items-center gap-12 pb-16 sm:gap-14 sm:pb-24 xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-14 xl:pb-28'>
                <div className='min-w-0'>
                    <Link
                        href='/company#freight-tech-100'
                        className='group/badge inline-flex max-w-full animate-fade-soft items-center gap-2 rounded-full border border-ink/10 bg-paper/70 py-1.5 pl-3 pr-1.5 text-[12.5px] text-ink-soft backdrop-blur transition-colors hover:border-ink/20 hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine sm:gap-2.5 sm:text-[13px]'
                    >
                        <LiveDot />

                        <span className='min-w-0 truncate'>
                            Named to the 2026 Freight Tech 100
                        </span>

                        <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-ink/[0.05] px-2 py-1 font-medium text-ink sm:px-2.5'>
                            <span className='sr-only sm:not-sr-only'>
                                Read more
                            </span>
                            <ArrowRight aria-hidden='true' className='size-3.5 transition-transform duration-300 group-hover/badge:translate-x-0.5' />
                        </span>
                    </Link>

                    <h1 id='hero-title' className='headline mt-7 text-[clamp(2.15rem,10.4vw,5.4rem)] text-ink xl:text-[min(5vw,4rem)]'>
                        <span className='block animate-hero-first [text-wrap:balance]'>
                            Every port connected.
                        </span>

                        <span className='block animate-hero-line [text-wrap:balance]'>
                            Every box{' '}
                            <span className='relative inline-block min-[400px]:whitespace-nowrap'>
                                accounted for.
                                <svg
                                    aria-hidden='true'
                                    viewBox='0 0 300 14'
                                    preserveAspectRatio='none'
                                    className='absolute -bottom-[0.1em] left-0 h-[0.16em] w-[96%]'
                                    fill='none'
                                >
                                    <path
                                        d='M 3 9 C 70 3, 150 2, 220 5 S 285 9, 297 6'
                                        pathLength={1}
                                        className='draw-once'
                                        stroke='#2f37ff'
                                        strokeWidth='3.2'
                                        strokeLinecap='round'
                                    />
                                </svg>
                            </span>
                        </span>
                    </h1>

                    <p className='mt-6 max-w-[36rem] text-[17px] leading-relaxed text-ink-soft animate-fade-soft sm:mt-7 sm:text-xl' style={{ animationDelay: '900ms' }}>
                        Quayside links your carriers, customs brokers, warehouses and ERPs into one live flow, and our team handles the exceptions so your freight keeps moving.
                    </p>

                    <div className='mt-9 flex flex-col gap-3 animate-fade-soft sm:flex-row sm:items-center' style={{ animationDelay: '1050ms' }}>
                        <ButtonLink href='/contact' size='lg'>
                            Book a walkthrough
                            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/btn:translate-x-0.5' />
                        </ButtonLink>

                        <Button
                            variant='outline'
                            size='lg'
                            onClick={() => setPlayerOpen(true)}
                            aria-haspopup='dialog'
                        >
                            <span className='grid size-6 place-items-center rounded-full bg-ink text-white'>
                                <Play aria-hidden='true' className='ml-px size-3 fill-current' />
                            </span>
                            Watch the flow
                        </Button>
                    </div>
                </div>

                <div className='animate-fade-soft' style={{ animationDelay: '400ms' }}>
                    <LaneCard />
                </div>
            </div>

            <LiveTicker />

            <FlowPlayerDialog open={playerOpen} onClose={() => setPlayerOpen(false)} />
        </section>
    )
}
